"use client"

import ArrowLSVG from "images/Icons/ForwardArrowL.inline.svg"
import ArrowMSVG from "images/Icons/ForwardArrowM.inline.svg"
import SmallArrowSVG from "images/Icons/SmallArrow.inline.svg"

import type { UniversalLinkProps } from "library/link"
import UniversalLink from "library/link"
import { css, fmobile, fresponsive, keyframes, styled } from "library/styled"
import { useAnimation } from "library/useAnimation"
import { type ComponentRef, type ReactNode, useRef } from "react"
import colors from "styles/colors"
import textStyles from "styles/text"

const ArrowHover = keyframes`
  0% {
    transform: translateX(0);
    z-index: 1;
  }
  1% {
    z-index: 0;
  }
  100% {
    transform: translateX(var(--arrow-left-offset));
    z-index: 0;
  }
`

const PillHover = keyframes`
  0% {
    transform: translateX(0);
    z-index: 0;
  }
  1% {
    z-index: 1;
  }
  100% {
    transform: translateX(var(--pill-right-offset));
    z-index: 1;
  }
`

const ArrowLeave = keyframes`
  0% {
    transform: translateX(var(--arrow-left-offset));
    z-index: 0;
  }
  1% {
    z-index: 1;
  }
  100% {
    transform: translateX(0);
    z-index: 1;
  }
`

const PillLeave = keyframes`
  0% {
    transform: translateX(var(--pill-right-offset));
    z-index: 1;
  }
  1% {
    z-index: 0;
  }
  100% {
    transform: translateX(0);
    z-index: 0;
  }
`

const colorTransition = css`
	transition:
		background-color 0.3s cubic-bezier(0.215, 0.61, 0.355, 1),
		color 0.3s cubic-bezier(0.215, 0.61, 0.355, 1),
		border 0.3s cubic-bezier(0.215, 0.61, 0.355, 1);
`

const pillAnimation = (animation: ReturnType<typeof keyframes>) => css`
	animation: ${animation} 0.3s forwards cubic-bezier(0.215, 0.61, 0.355, 1);
`

const arrowAnimation = (animation: ReturnType<typeof keyframes>) => css`
	animation: ${animation} 0.3s forwards cubic-bezier(0.215, 0.61, 0.355, 1);
`

const colorLibrary = {
	light: {
		normal: {
			background: colors.offWhite,
			text: colors.green400,
			border: colors.green400,
			hover: {
				background: colors.offWhite,
				text: colors.green200,
				border: colors.green200,
			},
			active: {
				background: colors.offWhite,
				text: colors.green200,
				border: colors.green300,
			},
		},
		simple: {
			background: colors.offWhite,
			text: colors.green400,
			border: colors.green400,
			hover: {
				background: colors.offWhite,
				text: colors.green200,
				border: colors.green200,
			},
			active: {
				background: colors.green100,
				text: colors.green400,
				border: colors.green400,
			},
		},
		arrow: {
			background: colors.offWhite,
			hover: {
				background: colors.offWhite,
			},
			active: {
				background: colors.offWhite,
			},
		},
	},
	dark: {
		normal: {
			background: colors.greenMain,
			text: colors.greenAccent,
			border: colors.greenAccent,
			hover: {
				background: colors.greenMain,
				text: colors.greenAccent,
				border: colors.greenAccent,
			},
			active: {
				background: colors.greenMain,
				text: colors.green100,
				border: colors.green100,
			},
		},
		simple: {
			background: colors.greenMain,
			text: colors.white,
			border: colors.green200,
			hover: {
				background: colors.greenMain,
				text: colors.green100,
				border: colors.green200,
			},
			active: {
				background: colors.greenMain,
				text: colors.green100,
				border: colors.green200,
			},
		},
		arrow: {
			background: colors.greenMain,
			hover: {
				background: colors.greenMain,
			},
			active: {
				background: colors.greenMain,
			},
		},
	},
}

export default function Button({
	children,
	size = "large",
	variant = "light",
	simple = false,
	...props
}: UniversalLinkProps & {
	children: ReactNode
	size?: "medium" | "large"
	variant?: "light" | "dark"
	simple?: boolean
}) {
	const wrapper = useRef<ComponentRef<"a"> & ComponentRef<"button">>(null)
	const pillRef = useRef<HTMLDivElement | null>(null)
	const arrowRef = useRef<HTMLDivElement | null>(null)
	const ArrowSVG = size === "medium" ? <ArrowM /> : <ArrowL />
	const buttonType = simple ? "simple" : "normal"

	useAnimation(
		() => {
			if (simple || !wrapper.current || !pillRef.current || !arrowRef.current)
				return

			const pill = pillRef.current
			const arrow = arrowRef.current
			const wrapperEl = wrapper.current

			const pillRect = pill.getBoundingClientRect()
			const arrowRect = arrow.getBoundingClientRect()
			const wrapperRect = wrapperEl.getBoundingClientRect()

			const pillRight = wrapperRect.right - pillRect.right
			const arrowLeft = arrowRect.left - wrapperRect.left

			wrapperEl.style.setProperty("--pill-right-offset", `${pillRight}px`)
			wrapperEl.style.setProperty("--arrow-left-offset", `${-arrowLeft}px`)
		},
		[simple],
		{ recreateOnResize: true },
	)

	return (
		<>
			<ArrowHover />
			<PillHover />
			<ArrowLeave />
			<PillLeave />
			<Wrapper
				{...props}
				ref={wrapper}
				variant={variant}
				simple={simple}
				size={size}
				buttonType={buttonType}
			>
				<Pill
					className="pill"
					ref={pillRef}
					variant={variant}
					buttonType={buttonType}
					size={size}
					simple={simple}
				>
					<TextContainer size={size}>{children}</TextContainer>
					{simple && (
						<SmallWrapper className="small-arrow" size={size}>
							<div>
								<SmallArrow size={size} />
							</div>
							<div>
								<SmallArrow size={size} />
							</div>
						</SmallWrapper>
					)}
				</Pill>
				{!simple && (
					<ArrowContainer
						size={size}
						className="arrow"
						variant={variant}
						ref={arrowRef}
					>
						{ArrowSVG}
					</ArrowContainer>
				)}
			</Wrapper>
		</>
	)
}

const Wrapper = styled(
	UniversalLink,
	({
		variant = "light",
		simple = false,
		buttonType = "normal",
		size = "large",
	}: {
		variant?: "light" | "dark"
		simple?: boolean
		buttonType?: "simple" | "normal"
		size?: "medium" | "large"
	}) => {
		const buttonColors = colorLibrary[variant][buttonType]

		return {
			...fresponsive(css`
				position: relative;
				display: flex;
				cursor: pointer;
				flex-shrink: 0;
				align-items: center;
				width: fit-content;
				gap: 6px;
				height: ${size === "large" ? "54px" : "36px"};

				@media (hover: hover) {
					&:hover .pill {
						background-color: ${buttonColors.hover.background};
						color: ${buttonColors.hover.text};
						border: 0.75px solid ${buttonColors.hover.border};
						${colorTransition}
						${!simple && pillAnimation(PillHover)};
					}

					&:hover .arrow {
						color: ${buttonColors.hover.text};
						${colorTransition}
						${!simple && arrowAnimation(ArrowHover)};
					}

					&:hover .small-arrow {
						color: ${buttonColors.hover.text};
					}

					&:hover .small-arrow-main {
						transform: translateY(-10px);
						opacity: 0;
					}

					&:hover .small-arrow-alt {
						transform: translateY(0);
						opacity: 1;
					}
				}

				.arrow {
					color: ${buttonColors.text};
					${colorTransition}
					${!simple && arrowAnimation(ArrowLeave)};
					will-change: transform;
				}

				.small-arrow {
					color: ${buttonColors.text};
				}

				&:active .pill {
					background-color: ${buttonColors.active.background};
					color: ${buttonColors.active.text};
					border: 0.75px solid ${buttonColors.active.border};
				}

				&:active .arrow {
					color: ${buttonColors.active.text};
				}

				&:active .small-arrow {
					color: ${buttonColors.active.text};
				}

				&:active .button-arrow {
					transform: translateX(80%);
					transition: transform 0.3s cubic-bezier(0.215, 0.61, 0.355, 1);
					opacity: 1;
				}

				&:active .button-arrow-alt {
					transform: translateX(0);
					transition: transform 0.3s cubic-bezier(0.215, 0.61, 0.355, 1);
					opacity: 1;
				}
			`),

			...fmobile(css`
				width: 100%;

				.arrow {
					width: 100%;
					max-width: 50px;
					text-align: center;
				}
			`),
		}
	},
)

const Pill = styled(
	"div",
	({
		variant = "light",
		buttonType = "normal",
		size = "large",
		simple = false,
	}: {
		variant?: "light" | "dark"
		buttonType?: "simple" | "normal"
		size?: "medium" | "large"
		simple?: boolean
	}) => {
		const buttonColors = colorLibrary[variant][buttonType]

		return {
			...fresponsive(css`
				position: relative;
				display: ${simple ? "flex" : ""};
				align-items: ${simple ? "center" : ""};
				gap: ${simple ? "2px" : ""};
				border-radius: 99vw;
				z-index: 0;
				isolation: isolate;
				border: 0.75px solid ${buttonColors.border};
				background-color: ${buttonColors.background};
				height: ${size === "large" ? "48px" : "32px"};
				color: ${buttonColors.text};
				${textStyles.bodyS};
				${size === "large"
					? buttonType === "normal"
						? css`
								${textStyles.bodyS};
								padding: 0 24px;
							`
						: css`
								${textStyles.bodyS};
								padding: 0 24px;
							`
					: css`
							${textStyles.bodyXXS};
							padding: 0 24px;
						`};
				${colorTransition}
				${!simple && pillAnimation(PillLeave)};
				will-change: transform, border;

				::before {
					height: 0;
				}

				::after {
					height: 0;
				}
			`),

			...fmobile(css`
				width: 100%;
				text-align: center;
			`),
		}
	},
)

const ArrowContainer = styled(
	"div",
	({
		variant = "light",
		size = "large",
	}: {
		variant?: "light" | "dark"
		size?: "medium" | "large"
	}) => {
		const arrowColors = colorLibrary[variant].arrow

		return {
			...fresponsive(css`
				position: relative;
				display: flex;
				overflow: hidden;
				align-items: center;
				justify-content: center;
				border-radius: 50%;
				z-index: 1;
				width: ${size === "medium" ? "32px" : "48px"};
				height: ${size === "medium" ? "32px" : "48px"};

				rect {
					fill: ${arrowColors.background};
				}

				svg {
					width: ${size === "medium" ? "32px" : "48px"};
					height: ${size === "medium" ? "32px" : "48px"};
				}
			`),
		}
	},
)

const TextContainer = styled(
	"div",
	({ size = "large" }: { size?: "medium" | "large" }) => ({
		...fresponsive(css`
			line-height: ${size === "large" ? "48px" : "32px"};
			height: ${size === "large" ? "48px" : "32px"};
		`),
		...fmobile(css`
			width: 100%;
			justify-content: center;
		`),
	}),
)

const SmallArrow = styled(
	SmallArrowSVG,
	({ size = "large" }: { size?: "medium" | "large" }) => {
		return {
			...fresponsive(css`
				width: ${size === "medium" ? "6px" : "8px"};
				height: ${size === "medium" ? "6px" : "8px"};
				position: absolute;
				top: 0;
				left: 0;

				.small-arrow-main {
					transform: translateY(0);
					opacity: 1;
					transition:
						transform 0.3s cubic-bezier(0.215, 0.61, 0.355, 1),
						opacity 0.3s cubic-bezier(0.215, 0.61, 0.355, 1);
				}

				.small-arrow-alt {
					transform: translateY(10px);
					opacity: 0;
					transition:
						transform 0.3s cubic-bezier(0.215, 0.61, 0.355, 1),
						opacity 0.3s cubic-bezier(0.215, 0.61, 0.355, 1);
				}
			`),
		}
	},
)

const ArrowL = styled(ArrowLSVG, {
	...fresponsive(css`
		position: relative;
		width: 48px;
		height: 48px;

		.button-arrow {
			transform: translateX(0);
			opacity: 1;
			transition:
				transform 0.3s cubic-bezier(0.215, 0.61, 0.355, 1),
				opacity 0.3s cubic-bezier(0.215, 0.61, 0.355, 1);
		}

		.button-arrow-alt {
			transform: translateX(-25px);
			opacity: 0;
			transition:
				transform 0.3s cubic-bezier(0.215, 0.61, 0.355, 1),
				opacity 0.3s cubic-bezier(0.215, 0.61, 0.355, 1);
		}
	`),
})

const ArrowM = styled(ArrowMSVG, {
	...fresponsive(css`
		width: 32px;
		height: 32px;

		.button-arrow {
			transform: translateX(0);
			opacity: 1;
			transition:
				transform 0.3s cubic-bezier(0.215, 0.61, 0.355, 1),
				opacity 0.3s cubic-bezier(0.215, 0.61, 0.355, 1);
		}

		.button-arrow-alt {
			transform: translateX(-15px);
			opacity: 0;
			transition:
				transform 0.3s cubic-bezier(0.215, 0.61, 0.355, 1),
				opacity 0.3s cubic-bezier(0.215, 0.61, 0.355, 1);
		}
	`),
})

const SmallWrapper = styled(
	"div",
	({ size = "large" }: { size?: "medium" | "large" }) => {
		return {
			...fresponsive(css`
				width: ${size === "medium" ? "6px" : "8px"};
				height: 10px;
				position: relative;
				left: 2px;
				overflow: hidden;
			`),
		}
	},
)
